import _ from 'lodash';
import { useMemo } from 'react';
import { useHasAccessToForm, useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { IPatientListDto, RouteProgressStateEnum, StudyRoleClaimValue } from 'src/services/api/api-client';
import { useGetStatisticTypesToExportQuery } from 'src/services/api/api-client/ExportLogQuery';
import { useGetFormsQuery } from 'src/services/api/api-client/FormsQuery';
import { FileTypeVariantsType, PatientReportVariantsType, StudyReportVariantsType } from './reportExporting';
import { HARDCODED_SURVEYS } from 'src/application/constants/hardoced-entities';

export type DataForExportingArgs = {
  patient?: IPatientListDto;
  reportVariant: StudyReportVariantsType | PatientReportVariantsType | undefined;
  withMissedSurveys?: boolean;
  fileType: FileTypeVariantsType | undefined;
};

export const useGetDataForExporting = ({
  patient,
  withMissedSurveys,
  fileType,
  reportVariant,
}: DataForExportingArgs) => {
  const { hasAccessToForm } = useHasAccessToForm();
  const { hasStudyClaim } = useHasStudyClaims();
  const study = useStudy();

  const { isInitialLoading, data } = useGetStatisticTypesToExportQuery(
    study!.id,
    patient?.uniqueId,
    fileType === 'Pdf' && withMissedSurveys,
    {
      enabled: hasStudyClaim(StudyRoleClaimValue.ExportForm) && reportVariant === 'Statistics',
      keepPreviousData: true,
    },
  );

  const formConfigIdForNotes = useMemo(
    () => (study?.groups.map((x) => x.formConfigIdForNotes).filter((x) => !!x && hasAccessToForm(x)) as number[]) ?? [],
    [hasAccessToForm, study?.groups],
  );

  const { data: noteForms } = useGetFormsQuery(
    {
      studyId: study!.id,
      ids: formConfigIdForNotes,
    },
    {
      enabled: !!study?.id && !!formConfigIdForNotes?.length,
    },
  );

  const formTypesToExport = useMemo(
    () => data?.formTypes.filter((ft) => !noteForms?.map((x) => x.type).includes(ft)),
    [data?.formTypes, noteForms],
  );

  const formTypes = useMemo(() => {
    if (fileType === 'Csv') {
      return formTypesToExport;
    }

    const missedTypes = patient?.studyRouteProgress.steps
      ?.filter((step) => step.state === RouteProgressStateEnum.Missed)
      .flatMap((step) => step.forms.filter((x) => x.formResultId === null).map((x) => x.formType) ?? []);

    return [...new Set([...(formTypesToExport ?? []), ...(missedTypes ?? [])])];
  }, [fileType, patient?.studyRouteProgress.steps, formTypesToExport]);

  const noteFormTypes = useMemo(() => {
    if (!noteForms?.length) return undefined;

    return noteForms.map((x) => x.type).filter((x) => data?.formTypes.includes(x));
  }, [data?.formTypes, noteForms]);

  return {
    isInitialLoading: isInitialLoading,
    formTypes: formTypes,
    surveyTypes: data?.surveyTypes?.filter((x) => x !== HARDCODED_SURVEYS.prismaCheck) ?? [],
    noteFormTypes: noteFormTypes,
  };
};
